import {
  changePlanStatusList,
  activePlanStatusList,
  restartPlanStatusList,
  IMMEDIATE_CANCEL_EXCEPTION_SKUS,
} from 'Utils/Constants';
import { checkSubscription } from 'Utils/Subscriptions';
import { getSubscription, getSubscriptionStatus, getChurnedRestartableSubscription } from 'Api/AccountApi';
import { getSingleDppSku } from 'Api/DppApi';
import { setPaymentTypeProperty, setSubscriptionStateProperty } from 'Utils/Analytics/Amplitude';
import { getState, setState } from 'Utils/Store';
import { isSheerIdPlan } from 'Utils/SheerIdHelpers';
import { isVeteransAdvPlan } from 'Utils/VeteransAdvHelpers';

export const subscriptionsInitialState = {
  activeSubscriptionId: '',
  activeSubscriptionPlanName: '',
  activeSubscriptionServiceName: '',
  activeSubscriptionStatus: '',
  activePaymentMethod: '',
  activeSubscriptionTier: 0,
  activeSubscriptionStartDate: '',
  accountPromotionSegment: {},
  hasActiveSubscription: false,
  hasSubscription: false,
  isChangePlanAvailable: false,
  isChurnedRestartAvailable: false,
  isChurnedUser: false,
  isExistingPlanUser: false,
  isFetching: false,
  isFetchingStatus: false,
  isFounder: false,
  isIAPUser: false,
  isNewUser: true,
  isPaused: false,
  isPendingPause: false,
  isPendingCancel: false,
  isImmediateCancel: false,
  isSuccess: false,
  isStatusSuccess: false,
  isTrialistUser: false,
  nextSubscription: null,
  pauseEndDate: 0,
  pauseStartDate: 0,
  purchaseType: '',
  restartChurnedUserDetails: {},
  validityTill: 0,
};

export const subscriptionsRequest = () => setState('subscription', { isFetching: true });
export const subscriptionStatusRequest = () => setState('subscription', { isFetchingStatus: true });

export const subscriptionsSuccess = (payload) =>
  setState('subscription', { isSuccess: true, isFetching: false, ...payload });
export const subscriptionsError = (payload) =>
  setState('subscription', { error: payload, isSuccess: true, isFetching: false });
export const subscriptionStatusSuccess = (payload) =>
  setState('subscription', { ...payload, isStatusSuccess: true, isFetchingStatus: false });
export const clearSubscription = () => setState('subscription', subscriptionsInitialState);

// helper function
const getPurchaseType = (paymentMethod = '', partner = '') => {
  switch (true) {
    case paymentMethod === 'Credit/Debit Card':
    case paymentMethod === 'PayPal Account':
    case paymentMethod === 'ApplePay':
      return 'web';
    case paymentMethod === 'Google Wallet':
    case paymentMethod === 'Google Payment':
      return 'android';
    case paymentMethod === 'Amazon App Store Billing':
      return 'amazon';
    case paymentMethod === 'App Store Billing':
      return 'ios';
    case paymentMethod === 'Roku Payment':
      return 'roku';
    case paymentMethod === 'Fox Partner Payment' && partner === 'Samsung Checkout':
      return 'samsung';
    //Some early bango accounts don't have a partner value
    case paymentMethod === 'Fox Partner Payment' && (!partner || partner === 'Verizon'):
      return 'bango';
    case paymentMethod === 'Coupon':
      return 'coupon';
    case paymentMethod === 'Fox Payment':
      return 'gift';
    default:
      return paymentMethod;
  }
};

export const fetchSubscriptions = async () => {
  const {
    appConfig: {
      defaultConfig: { productSkus },
    },
  } = getState();

  subscriptionsRequest();
  try {
    const subs = await getSubscription(productSkus);
    const { error = '' } = subs;
    if (error) {
      throw new Error('Error: Cannot fetch user subscription');
    } else {
      const sub = checkSubscription(subs);
      subscriptionsSuccess(sub);
    }
  } catch (error) {
    subscriptionsError(error.message);
    console.error(error);
  }
};

export const fetchSubscriptionStatus = async () => {
  const {
    subscription: { validityTill },
  } = getState();

  subscriptionStatusRequest();
  try {
    const data = await getSubscriptionStatus();

    if (!data) {
      throw new Error('Error: Cannot fetch user subscription status');
    } else {
      const [currentSubscription = {}] = data;
      if (currentSubscription && currentSubscription.status) {
        const serviceId = currentSubscription.serviceID;
        const activeSubscriptionStatus = currentSubscription.status || '';
        const activeSubscriptionPlanName = currentSubscription.displayName || '';
        const activeSubscriptionServiceName = currentSubscription.serviceName || '';
        const activePaymentMethod = currentSubscription.paymentMethod || '';
        const activeSubscriptionId = serviceId || '';
        const isChangePlanAvailable = changePlanStatusList.includes(activeSubscriptionStatus);
        const hasActiveSubscription = activePlanStatusList.includes(activeSubscriptionStatus);
        const isPendingCancel = serviceId ? restartPlanStatusList.includes(activeSubscriptionStatus) : false;
        const isExistingPlanUser = activeSubscriptionStatus === 'Existing';
        const isTrialistUser = activeSubscriptionStatus.includes('Trialist');
        const isImmediateCancel =
          isTrialistUser &&
          !IMMEDIATE_CANCEL_EXCEPTION_SKUS.includes(activeSubscriptionId) &&
          !isSheerIdPlan(activeSubscriptionId) &&
          !isVeteransAdvPlan(activeSubscriptionId);
        const isNewUser = activeSubscriptionStatus === 'New'; // New user is w/o subscription, churned user have different status.
        const isPendingPause = activeSubscriptionStatus === 'Pending Pause';
        const isPaused = activeSubscriptionStatus === 'Pause';
        const isChurnedUser = activeSubscriptionStatus.includes('Churned');
        const pauseStartDate = currentSubscription.pauseStartDate || 0;
        const pauseEndDate = currentSubscription.pauseEndDate || 0;
        const purchaseType = getPurchaseType(activePaymentMethod, currentSubscription.partner);
        const isIAPUser = ['ios', 'android', 'roku', 'bango', 'amazon'].includes(purchaseType);
        const activeSubscriptionTier = currentSubscription.tier || 0;
        // This will need to be changed if we ever start receiving more than one promotional segment
        const accountPromotionSegment = currentSubscription.segments ? currentSubscription.segments[0] : {};

        let isChurnedRestartAvailable = false;
        let restartChurnedUserDetails = {};

        if (hasActiveSubscription || isChurnedUser) {
          setPaymentTypeProperty(currentSubscription.paymentMethod);
          setSubscriptionStateProperty(currentSubscription.status);
        }

        if (isChurnedUser) {
          try {
            const { isEligibleForRestart, ...churnedDetails } = await getChurnedRestartableSubscription();
            isChurnedRestartAvailable = isEligibleForRestart;
            const restartPlanId = churnedDetails?.restartPlan?.packageID;
            const dppContent = restartPlanId && (await getSingleDppSku(restartPlanId));
            const updatedChurnedDetails = {
              ...churnedDetails,
              restartPlan: {
                ...churnedDetails.restartPlan,
                dppContent,
              },
            };
            restartChurnedUserDetails = updatedChurnedDetails;
          } catch (error) {
            console.error(error);
          }
        }

        const statusData = {
          activeSubscriptionId,
          activePaymentMethod,
          activeSubscriptionPlanName,
          activeSubscriptionServiceName,
          activeSubscriptionStatus,
          hasActiveSubscription,
          isChangePlanAvailable,
          isChurnedRestartAvailable,
          isChurnedUser,
          isExistingPlanUser,
          isNewUser,
          isIAPUser,
          isPaused,
          isPendingPause,
          isPendingCancel,
          isImmediateCancel,
          isTrialistUser,
          pauseEndDate,
          pauseStartDate,
          purchaseType,
          restartChurnedUserDetails,
          activeSubscriptionTier,
          accountPromotionSegment,
        };

        // if /hasSubscription endpoint did not provide a value for validityTill or if it's 0, set the value from this
        //    endpoint instead (e.g. in cases such as churned gift users where normally other endpoints do not supply
        //    a churned date since there is no plan data)
        if (!validityTill) {
          statusData.validityTill = currentSubscription.validityTill;
        }

        subscriptionStatusSuccess(statusData);
      }
      return currentSubscription;
    }
  } catch (error) {
    console.error(error);
    return {};
  }
};
